import { GameEntity, GameStatus, Identifier, PlatformType } from '@okletsplay/core';
import { createSelector } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { AppState } from 'app/interfaces/store';

import { entityAdapter } from 'modules/games/store/games/adapter';
import { useMemo } from 'react';
import { store } from 'redux/store';

const GAME_ORDER: Partial<Record<PlatformType, number>> = {
  [PlatformType.Lichess]: 1,
  [PlatformType.Lidraughts]: 2,
  [PlatformType.Olp]: 3,
  [PlatformType.Colyseus]: 4,
  [PlatformType.EvIo]: 5,
  [PlatformType.LeagueOfLegends]: 6,
  [PlatformType.BubbleUpEndless]: 7,
};

const TOURNAMENT_GAME_PLATFORMS = [
  PlatformType.LeagueOfLegends,
  PlatformType.Lichess,
  PlatformType.EvIo,
  PlatformType.Olp,
  PlatformType.Colyseus,
  PlatformType.Papergames,
  PlatformType.BubbleUpEndless,
];

const MATCH_GAME_PLATFORMS = [
  PlatformType.Lidraughts,
  PlatformType.Lichess,
  PlatformType.EvIo,
  PlatformType.Olp,
  PlatformType.Colyseus,
  PlatformType.Papergames,
  PlatformType.BubbleUpEndless,
];

const gamesState = (state: AppState) => state[EntityStoreName.Games];

const selectAll = entityAdapter.getSelectors<AppState>(
  (state) => state[EntityStoreName.Games]
).selectAll;

const selectAllSelector = createSelector(selectAll, (games: GameEntity[] = []) => {
  return games.sort((first, second) =>
    first.status === second.status ? 0 : first.status === GameStatus.Enabled ? -1 : 1
  );
});

const selectEnabled = createSelector(selectAll, (games: GameEntity[] = []) => {
  return games
    .filter((game: GameEntity) => game?.status === GameStatus.Enabled)
    .map((game) => {
      return { ...game, order: GAME_ORDER[game.platformType] };
    });
});

const selectMatchGames = createSelector(selectEnabled, (games: GameEntity[] = []) => {
  return games.filter((game: GameEntity) => game?.isMatch);
});

const selectTournamentGames = createSelector(selectEnabled, (games: GameEntity[] = []) => {
  return games.filter((game: GameEntity) => game?.isTournament);
});

const selectByIdFactory = (id: string) =>
  createSelector(store.getState, (state) =>
    entityAdapter.getSelectors<AppState>(gamesState).selectById(state, String(id))
  );

const selectIsTournamentsSupportedFactory = (id: Identifier) =>
  createSelector(store.getState, (state) => {
    const game = entityAdapter.getSelectors<AppState>(gamesState).selectById(state, String(id));
    if (!game) {
      return false;
    }

    return TOURNAMENT_GAME_PLATFORMS.includes(game.platformType);
  });

const selectIsMatchesSupportedFactory = (id: Identifier) =>
  createSelector(store.getState, (state) => {
    const game = entityAdapter.getSelectors<AppState>(gamesState).selectById(state, String(id));
    if (!game) {
      return false;
    }

    return MATCH_GAME_PLATFORMS.includes(game.platformType);
  });

export const gamesSelector = {
  all: selectAllSelector,
  enabled: selectEnabled,
  matchGames: selectMatchGames,
  isAsyncGame: selectMatchGames,
  tournamentGames: selectTournamentGames,
};

export const gamesSelectorFactory = {
  useById: (id: string) => useMemo(() => selectByIdFactory(id), [id]),
  useIsTournamentsSupported: (id: string) =>
    useMemo(() => selectIsTournamentsSupportedFactory(id), [id]),
  useIsMatchesSupported: (id: string) => useMemo(() => selectIsMatchesSupportedFactory(id), [id]),
};
